import React, { FormEvent, useState, useContext, useEffect } from "react";
import {
  TextField,
  Button,
  Container,
  useTheme,
  Paper,
  Typography,
  CircularProgress,
  useMediaQuery,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box } from "@mui/system";
import { useAppDispatch } from "../../hooks";
import { setLoggedInUser } from "../../reducers/userSlice";
import { AuthContext } from "../../providers/AuthProvider";
import { login } from "./LoginRepository";
import { useNavigate } from "react-router-dom";
import { OVERVIEW_ROUTE } from "../../Routes";
import { showSnackbar } from "../../reducers/snackbarSlice";
import { useTranslation } from "react-i18next";
import { SecurityLevel } from "../../types/User";
import { APP_VERSION } from "../../config";

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<LoginFormData>({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, isAuthenticated, setUser, setIsAuthenticated } = useContext(AuthContext);

  const isFormValid = formData.email !== "" && formData.password !== "";

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(OVERVIEW_ROUTE.path, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!isFormValid) {
      dispatch(showSnackbar({ message: t("login.fill_all_fields"), severity: "error" }));
      return;
    }

    setIsLoading(true);
    try {
      const loggedInUser = await login({ email: formData.email, password: formData.password });

      if (loggedInUser.securityLevel < SecurityLevel.PlatformAdmin) {
        dispatch(showSnackbar({ message: t("login.failed"), severity: "error" }));
        return;
      }

      dispatch(setLoggedInUser(loggedInUser));
      setUser(loggedInUser);
      setIsAuthenticated(true);
      navigate(OVERVIEW_ROUTE.path, { replace: true });
    } catch (errorMessage) {
      console.error(errorMessage);
      dispatch(showSnackbar({ message: t("login.failed"), severity: "error" }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xl" disableGutters>
      <Box display="flex" flexDirection={isSmallScreen ? "column" : "row"} height="100vh">
        {!isSmallScreen && (
          <Box
            flex="0 0 60%"
            style={{
              backgroundImage: `linear-gradient(20deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="h2" sx={{ color: theme.palette.primary.contrastText }}>
              {t("login.welcome")}
            </Typography>
          </Box>
        )}
        <Box
          flex={isSmallScreen ? "1 0 auto" : "0 0 40%"}
          component={Paper}
          elevation={6}
          display="flex"
          flexDirection="column"
          alignItems="center"
          p={4}
          square
          height={isSmallScreen ? "auto" : "100%"}
        >
          <Box alignItems="flex-start" justifyContent="center" display="flex" flexDirection="column" flexGrow={1}>
            <Typography variant="h3">{t("login.title")}</Typography>
            <form noValidate onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="email"
                label={t("login.email_label")}
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleInputChange}
                data-cy="email"
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                name="password"
                label={t("login.password_label")}
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                data-cy="password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t("login.toggle_password_visibility")}
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isLoading}
                sx={{ mt: 3 }}
                data-cy="login"
              >
                {t("login.button")}
                {isLoading && <CircularProgress size={22} sx={{ ml: 3 }} />}
              </Button>
            </form>
          </Box>
          <Box sx={{ mt: 4, alignSelf: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {t("login.version_label")}: {APP_VERSION}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
